import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {TrailRating} from "types/TrailRating";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import {formattedDate} from "services/date";
import DeleteTrailRating from "pages/TrailRating/Delete";
import StatBox from "components/StatBox";
import {MapIcon} from "@heroicons/react/outline";
import StatList from "components/StatList";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";

function ViewTrailRating() {
  const navigate = useNavigate();
  const auth = useAuth();
  const {id} = useParams();

  const [deletingTrailRating, setDeletingTrailRating] = useState<TrailRating | null>(null);

  const {data: trailRating, isLoading} = useQuery(
    ['trail-rating', {id}],
    () => ngage.trailRatings.get(Number(id)),
    {enabled: auth.can('trail-ratings:read')}
  );

  // Prepare action handlers
  function deleteTrailRating() {
    if (trailRating) setDeletingTrailRating(trailRating)
  }

  function editTrailRating() {
    if (id) navigate(`/trail-ratings/${id}/edit`)
  }

  // Prevent unauthorised viewing
  if (auth.cannot('trail-ratings:read')) {
    return <NotAuthorised/>
  }

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('trail-ratings:write') &&
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={deleteTrailRating}>Delete</Button>
      }
      {auth.can('trail-ratings:write') &&
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={editTrailRating}>Edit</Button>
      }
    </>
  )

  const breadcrumbs = {
    "Content": "/",
    "Trails": "/trails",
    "Trail Ratings": "/trail-ratings",
    [trailRating?.name || "View Trail Rating"]: `/trail-ratings/${id}`
  }

  return (
    <Page
      title={trailRating?.name || "View Trail Rating"}
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >
      {!!trailRating &&
        <>
          <section>
            <StatList>
              <StatBox name="Trails" icon={MapIcon} stat={trailRating.trail_count}/>
            </StatList>
          </section>

          <section className="mt-4">
            <Card
              heading="Trail Rating Information"
              subheading={`A rating used for ${trailRating.trail_count} trails`}
            >
              <DescriptionList>
                <DescriptionListItem span={2}>
                  <DescriptionTerm>Description</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">{trailRating.description ?? 'N/A'}</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Colour</DescriptionTerm>
                  <DescriptionDetails>
                    <span
                      className="block border border-1 border-gray-400 rounded w-16 h-6"
                      style={{backgroundColor: trailRating.colour}}
                    />
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Created</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(trailRating.created_at)}
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteTrailRating
        trailRating={deletingTrailRating}
        show={trailRating === deletingTrailRating}
        onCancel={() => setDeletingTrailRating(null)}
        onDelete={() => navigate('/trail-ratings')}
        onDeleteFail={() => setDeletingTrailRating(null)}
      />
    </Page>
  );
}

export default ViewTrailRating;